/**
 * Combobox autocomplete pro campo "cidade de origem" no Step 1.
 *
 * Visual e UX alinhados com o `<EventCityAutocomplete>` do admin —
 * implementação separada porque o widget público não importa nada do
 * monorepo admin (bundle leve).
 *
 * UX:
 *   - Debounce 300ms
 *   - Mínimo 2 chars (Nominatim faz match por palavra inteira; 1 char não rola)
 *   - Keyboard nav: ↑ / ↓ navega · Enter seleciona highlight (NÃO submete o form
 *     quando dropdown está aberto) · Esc fecha
 *   - Click fora fecha o dropdown
 *   - Sugestões dedupadas por (name, region, country)
 *
 * O fix do Enter (issue do user: "ao apertar enter pra ir pro próximo
 * campo ele me mandou pra próxima página") está em `onKey` — se dropdown
 * está aberto com sugestões, `Enter` chama `preventDefault()` antes do
 * form.submit nativo capturar o keypress.
 */
import { useEffect, useRef, useState } from 'react';
import type { CalculatorClient } from '../api/client';
import type { CitySuggestion } from '../api/types';
import { TextInput } from './FormControls';

interface Props {
  value: string;
  onChange: (v: string) => void;
  /** Disparado quando o user escolhe explicitamente uma sugestão (click/Enter).
   * Útil pra disparar /distance assim que a cidade for confirmada. */
  onPick?: (s: CitySuggestion) => void;
  client?: CalculatorClient;
  token?: string;
  placeholder?: string;
  /** Atributo de acessibilidade — id do label do input. */
  id?: string;
  /** Pra desabilitar (modo somente leitura). */
  disabled?: boolean;
}

export function CityAutocomplete({
  value, onChange, onPick, client, token, placeholder, id, disabled,
}: Props) {
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<CitySuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastQueryRef = useRef<string>('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch suggestions com debounce.
  useEffect(() => {
    if (!client || !token) return;
    const q = value.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (q.length < 2) {
      setSuggestions([]);
      setLoading(false);
      setOpen(false);
      return;
    }
    if (q === lastQueryRef.current) return;

    debounceRef.current = setTimeout(() => {
      setLoading(true);
      const ctrl = new AbortController();
      client.searchCities(token, q, ctrl.signal)
        .then((res) => {
          setSuggestions(res.suggestions);
          setHighlight(0);
          setOpen(res.suggestions.length > 0);
        })
        .catch(() => {
          setSuggestions([]);
          setOpen(false);
        })
        .finally(() => setLoading(false));
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, client, token]);

  // Click fora fecha o dropdown.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function selectSuggestion(s: CitySuggestion) {
    // Guarda "Cidade, UF": sem a UF o backend geocodifica só o nome e o
    // Nominatim devolve o homônimo de maior ranking (Palmas TO×PR,
    // Valença BA×RJ), fazendo a distância sair da cidade errada em silêncio.
    const composed = s.region ? `${s.name}, ${s.region}` : s.name;
    // lastQueryRef precisa receber o valor composto: onChange muda `value`,
    // o effect roda de novo e, se lastQuery ficasse só com s.name, dispararia
    // um refetch do dropdown logo após a seleção.
    lastQueryRef.current = composed;
    onChange(composed);
    setOpen(false);
    onPick?.(s);
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open || suggestions.length === 0) {
      // Enter no input vazio/sem dropdown: deixa o comportamento padrão do form.
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      // **Bug fix do user**: dropdown aberto → Enter seleciona sugestão,
      // NÃO submete o form. Sem preventDefault, o form.onSubmit captura
      // o Enter primeiro e avança pro Step 2 antes do user escolher.
      e.preventDefault();
      selectSuggestion(suggestions[highlight]!);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <TextInput
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder ?? 'Comece a digitar (ex.: São Paulo)…'}
        maxLength={80}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={id ? `${id}-listbox` : undefined}
        disabled={disabled}
      />

      {open && (
        <ul
          id={id ? `${id}-listbox` : undefined}
          role="listbox"
          className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-auto rounded-md bg-white border border-areia shadow-lg"
        >
          {loading && (
            <li className="px-3 py-2 text-2xs text-tabaco">Buscando…</li>
          )}
          {!loading && suggestions.length === 0 && (
            <li className="px-3 py-2 text-2xs text-tabaco">Nenhuma cidade encontrada.</li>
          )}
          {suggestions.map((s, i) => (
            <li
              key={`${s.name}-${s.region}-${i}`}
              role="option"
              aria-selected={i === highlight}
              onMouseDown={(e) => {
                e.preventDefault();
                selectSuggestion(s);
              }}
              onMouseEnter={() => setHighlight(i)}
              className={
                'px-3 py-2 cursor-pointer text-sm transition-colors ' +
                (i === highlight ? 'bg-floresta/10 text-floresta' : 'hover:bg-nevoa/40')
              }
            >
              <span>{s.name}</span>
              {(s.region || s.country) && (
                <span className="text-2xs text-tabaco ml-2">
                  {[s.region, s.country].filter(Boolean).join(' · ')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
